import { OnboardingPaywallModalCallable } from "@components/OnboardingWizard/Paywall/OnboardingPaywallModalCallable";
import { callEdgenModalWhenRootReadyAsync } from "@components/EdgenModal/waitForEdgenModalRoot";
import {
  ACCOUNTS_RECONNECT_PAYWALL_COPY_OVERRIDES,
  ACCOUNTS_RECONNECT_PAYWALL_TRACKING_MODULE,
  ACCOUNT_WATCH_PAYWALL_COPY_OVERRIDES,
  ACCOUNT_WATCH_PAYWALL_TRACKING_MODULE,
  GOAL_ACTIVATE_PAYWALL_COPY_OVERRIDES,
  GOAL_ACTIVATE_PAYWALL_TRACKING_MODULE,
  ONBOARDING_PAYWALL_COPY_OVERRIDES,
  ONBOARDING_PAYWALL_TRACKING_MODULE,
  TRACKER_PAYWALL_COPY_OVERRIDES,
  TRACKER_PAYWALL_TRACKING_MODULE,
} from "./constants/onboardingPaywallCopy";
import { ONBOARDING_PAYWALL_MODAL_Z_INDEX } from "@components/OnboardingWizard/PlanOverviewModal/planOverviewModalShell";
import type { OnboardingPaywallOutcome } from "@/src/hooks/onboarding/onboardingV3Types";
import { getBridge, waitForAppFeatures } from "@/src/lib/bridge";
import { MessageType } from "@/src/lib/bridge/types";
import { parseOnboardingHarnessQuery } from "@hooks/onboarding/onboardingHarnessQuery";

type OnboardingPaywallVariant =
  | "onboarding_first"
  | "goal_activate_second"
  | "accounts_reconnect"
  | "account_watch"
  | "tracker";

export interface OpenOnboardingPaywallOptions {
  onboardingFirstWall?: boolean;
  restoredPlanId?: string;
  checkoutPending?: boolean;
  onClose?: () => void;
  paywallVariant?: OnboardingPaywallVariant;
  /** 覆盖 Paywall analytics source（如 tracker / goal_activate） */
  analyticsSource?: string;
  /** Goal activation context is forwarded to native Paywall for the same attempt. */
  goalId?: string;
  source?: "chat" | "signal" | "plan" | "my_goals";
  /** User clicked web pay CTA and got redirected to pricing. */
  onWebCheckoutRedirect?: () => void;
}

const resolvePaywallVariantConfig = (variant: OnboardingPaywallVariant) => {
  if (variant === "goal_activate_second") {
    return {
      trackingModuleName: GOAL_ACTIVATE_PAYWALL_TRACKING_MODULE,
      paywallCopyOverrides: GOAL_ACTIVATE_PAYWALL_COPY_OVERRIDES,
      defaultAnalyticsSource: "goal_activate",
    };
  }
  if (variant === "accounts_reconnect") {
    return {
      trackingModuleName: ACCOUNTS_RECONNECT_PAYWALL_TRACKING_MODULE,
      paywallCopyOverrides: ACCOUNTS_RECONNECT_PAYWALL_COPY_OVERRIDES,
      defaultAnalyticsSource: "accounts_reconnect",
    };
  }
  if (variant === "tracker") {
    return {
      trackingModuleName: TRACKER_PAYWALL_TRACKING_MODULE,
      paywallCopyOverrides: TRACKER_PAYWALL_COPY_OVERRIDES,
      defaultAnalyticsSource: "tracker",
    };
  }
  if (variant === "account_watch") {
    return {
      trackingModuleName: ACCOUNT_WATCH_PAYWALL_TRACKING_MODULE,
      paywallCopyOverrides: ACCOUNT_WATCH_PAYWALL_COPY_OVERRIDES,
      defaultAnalyticsSource: "account_watch",
    };
  }
  return {
    trackingModuleName: ONBOARDING_PAYWALL_TRACKING_MODULE,
    paywallCopyOverrides: ONBOARDING_PAYWALL_COPY_OVERRIDES,
    defaultAnalyticsSource: "onboarding",
  };
};

async function tryDelegateOnboardingPaywallToNative(
  config: ReturnType<typeof resolvePaywallVariantConfig>,
  options: OpenOnboardingPaywallOptions | undefined,
): Promise<OnboardingPaywallOutcome | undefined> {
  if (!getBridge().isInApp()) {
    return undefined;
  }
  const features = await waitForAppFeatures();
  if (
    features?.nativePaywall !== true ||
    (options?.onboardingFirstWall && features.nativeOnboardingPaywall !== true)
  ) {
    return undefined;
  }
  // This request includes the user's time choosing a plan and completing store authentication.
  const response = await getBridge().request(
    MessageType.OPEN_NATIVE_PAYWALL,
    {
      variant: options?.onboardingFirstWall
        ? "onboarding_first"
        : "goal_activate_second",
      module: config.trackingModuleName,
      analyticsSource: config.defaultAnalyticsSource,
      goalId: options?.goalId,
      source: options?.source,
    },
    { timeoutMs: 30 * 60_000 },
  );
  const data = response?.data;
  return data?.purchased === true || data === true ? "purchased" : "dismissed";
}

/** 免费用户订阅 Paywall；`purchased` = 支付成功 */
export const openOnboardingPaywall = async (
  isPC: boolean,
  options?: OpenOnboardingPaywallOptions,
): Promise<OnboardingPaywallOutcome> => {
  const variant = options?.paywallVariant ?? "onboarding_first";
  const config = resolvePaywallVariantConfig(variant);
  const analyticsSource =
    options?.analyticsSource ?? config.defaultAnalyticsSource;

  const pricingPreview =
    options?.onboardingFirstWall &&
    typeof window !== "undefined" &&
    parseOnboardingHarnessQuery(new URLSearchParams(window.location.search))
      .pricingFixture;

  if (
    !pricingPreview &&
    !(options?.onboardingFirstWall && options.checkoutPending) &&
    (variant === "goal_activate_second" || options?.onboardingFirstWall)
  ) {
    const nativeOutcome = await tryDelegateOnboardingPaywallToNative(
      config,
      options,
    );
    if (nativeOutcome) {
      return nativeOutcome;
    }
  }

  return callEdgenModalWhenRootReadyAsync(async () => {
    const result = await OnboardingPaywallModalCallable({
      isPC,
      ...(options?.onboardingFirstWall
        ? {
            onboardingFirstWall: true,
            restoredPlanId: options.restoredPlanId,
            checkoutPending: options.checkoutPending,
          }
        : {}),
      trackingModuleName: config.trackingModuleName,
      paywallCopyOverrides: config.paywallCopyOverrides,
      analyticsSource,
      ...(variant === "goal_activate_second"
        ? { paywallVariant: "goal_activate_second" as const }
        : {}),
      zIndex: ONBOARDING_PAYWALL_MODAL_Z_INDEX,
      onClose: options?.onClose,
      onWebCheckoutRedirect: options?.onWebCheckoutRedirect,
    });

    return result === true ? "purchased" : "dismissed";
  });
};
